import { useState, useEffect } from "react";

/**
 * Custom hook for species filtering and image management
 * Consolidates filter state, area mapping, and image fetching
 * 
 * @param {Array} species - Species data from API
 * @param {Array} areas - Areas data from API
 * @returns {Object} Filter state and computed values
 */
export function useSpeciesFiltering(species, areas, initialSelectedArea = "") {
    const [selectedArea, setSelectedArea] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [speciesAreaMap, setSpeciesAreaMap] = useState({});
    const [speciesImages, setSpeciesImages] = useState({});
    const [loadingImages, setLoadingImages] = useState(true);

    useEffect(() => {
        if (initialSelectedArea) {
            setSelectedArea(initialSelectedArea);
        }
    }, [initialSelectedArea]);

    // Fetch species-area relationships
    useEffect(() => {
        if (areas && areas.length > 0) {
            const fetchSpeciesForAreas = async () => {
                const areaSpeciesMap = {};
                
                for (const area of areas) {
                    try {
                        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${area.id}`);
                        const areaSpecies = await response.json();
                        
                        areaSpecies.forEach(specie => {
                            if (!areaSpeciesMap[specie.id]) {
                                areaSpeciesMap[specie.id] = [];
                            }
                            areaSpeciesMap[specie.id].push(area.name);
                        });
                    } catch (error) {
                        console.error(`Error fetching species for area ${area.id}:`, error);
                    }
                }
                
                setSpeciesAreaMap(areaSpeciesMap);
            };
            
            fetchSpeciesForAreas();
        }
    }, [areas]);

    // Fetch images on demand (lazy loading approach)
    // Instead of fetching all at once, images will be fetched per species
    // This is handled in a separate function that can be called as needed
    const fetchSpeciesImage = async (specieId) => {
        // Skip if already loading or loaded
        if (speciesImages[specieId] !== undefined) return;
        
        // Mark as loading
        setSpeciesImages(prev => ({ ...prev, [specieId]: 'loading' }));
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${specieId}/images`);
            
            if (response.ok) {
                const data = await response.json();
                
                if (data.images && data.images.length > 0) {
                     const imageUrls = data.images.map(img => {
                        const base64String = img.base64;

                        return base64String.startsWith('data:')
                            ? base64String
                            : `data:image/jpeg;base64,${base64String}`;
                    });
                    
                    setSpeciesImages(prev => ({ ...prev, [specieId]: imageUrls }));
                    return;
                }
            }
            
            // No image found (404 or empty response)
            setSpeciesImages(prev => ({ ...prev, [specieId]: null }));
            
        } catch (error) {
            console.error(`Error fetching image for species ${specieId}:`, error);
            setSpeciesImages(prev => ({ ...prev, [specieId]: null }));
        }
    };
    
    // Initial loading state management
    useEffect(() => {
        if (species && species.length > 0) {
            setLoadingImages(false);
        }
    }, [species]);

    // Handle scroll for scroll-to-top button
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 400);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleAreaFilterChange = (event) => {
        setSelectedArea(event.target.value);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Filter species by search term, category, and area
    const filteredSpecies = species?.filter((s) => {
        const matchesSearch = !searchTerm || s.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !selectedCategory || s.category_name === selectedCategory;
        const speciesAreas = speciesAreaMap[s.id] || [];
        const matchesArea = !selectedArea || speciesAreas.includes(selectedArea);
        
        return matchesSearch && matchesCategory && matchesArea;
    });

    // Group species by category
    const groupedSpecies = filteredSpecies?.reduce((acc, specie) => {
        const category = specie.category_name || "Övrigt";
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(specie);
        return acc;
    }, {});

    // Get unique categories for filter dropdown
    const categories = [...new Set(species?.map(s => s.category_name).filter(Boolean))];

    return {
        selectedArea,
        selectedCategory,
        searchTerm,
        setSearchTerm,
        setSelectedCategory,
        showScrollTop,
        speciesAreaMap,
        speciesImages,
        loadingImages,
        handleAreaFilterChange,
        scrollToTop,
        filteredSpecies,
        groupedSpecies,
        categories,
        fetchSpeciesImage, // Expose for lazy loading
    };
}
